import React, {ReactNode, useEffect, useState} from 'react';
import {classes, st} from '../../../common/components/Layout/Layout.st.css';
import {MainLoader} from '../../../common/components/Layout/MainLoader/MainLoader';
import {AsideLoader} from '../../../common/components/Layout/AsideLoader/AsideLoader';
import {HeaderLoader} from '../../../common/components/Layout/HeaderLoader/HeaderLoader';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {A11yIds, SPECS} from '../../../common/constants';
import {useLocaleKeys} from '../../../locale-keys/LocaleKeys';

export enum LayoutDataHooks {
  mainSection = 'LayoutDataHooks.mainSection',
  oneColumnViewSummary = 'LayoutDataHooks.oneColumnViewSummary',
  twoColumnViewSummary = 'LayoutDataHooks.twoColumnViewSummary',
  verticalDivider = 'LayoutDataHooks.verticalDivider',
}

export interface LayoutProps {
  isLoading: boolean;
  showErrorState?: boolean;
  isOneColumnView?: boolean;
  Header: () => ReactNode;
  Aside: () => ReactNode;
  Footer: () => ReactNode;
  ErrorState: () => ReactNode;
  children: ReactNode;
  dataHook: string;
  dir: string;
}

/* eslint-disable sonarjs/cognitive-complexity */
export const Layout = ({
  dataHook,
  dir,
  isLoading,
  showErrorState,
  isOneColumnView,
  Header,
  Aside,
  Footer,
  ErrorState,
  children,
}: LayoutProps) => {
  const localeKeys = useLocaleKeys();
  const [loaded, setLoaded] = useState(false);
  const {experiments} = useExperiments();
  const ecomFitCheckoutLayoutOnTablet = experiments.enabled(SPECS.ecomFitCheckoutLayoutOnTablet);
  const asideClassName = ecomFitCheckoutLayoutOnTablet ? classes.aside : /* istanbul ignore next */ classes.asideOld;
  const summaryContainerClassName = ecomFitCheckoutLayoutOnTablet
    ? classes.summaryContainer
    : /* istanbul ignore next */ classes.summaryContainerOld;

  useEffect(() => {
    setLoaded(!isLoading);
  }, [isLoading]);

  return (
    <div
      data-hook={dataHook}
      className={st(classes.root, showErrorState ? classes.errorStateContainer : undefined)}
      dir={dir}>
      {loaded || showErrorState ? Header() : <HeaderLoader />}
      {showErrorState ? (
        ErrorState()
      ) : (
        <div className={classes.mainContainer}>
          <div className={classes.container}>
            {isOneColumnView && (
              <div className={summaryContainerClassName}>
                <aside data-hook={LayoutDataHooks.oneColumnViewSummary} className={asideClassName}>
                  {loaded ? Aside() : null}
                </aside>
              </div>
            )}
            <div className={classes.formContainer}>
              <section
                className={classes.main}
                data-hook={LayoutDataHooks.mainSection}
                aria-label={localeKeys.checkout.checkoutForm.ariaLabel()}>
                {loaded ? children : <MainLoader />}
              </section>
            </div>
            {!isOneColumnView && <div data-hook={LayoutDataHooks.verticalDivider} className={classes.divider} />}
            {(!isOneColumnView || !loaded) && (
              <div className={summaryContainerClassName}>
                <section
                  aria-labelledby={A11yIds.SummarySection}
                  data-hook={LayoutDataHooks.twoColumnViewSummary}
                  className={asideClassName}>
                  {loaded ? Aside() : <AsideLoader />}
                </section>
              </div>
            )}
          </div>
        </div>
      )}
      {loaded && Footer()}
    </div>
  );
};
